/**
 * StuckJobCancelModal — cancel a stuck in-progress job (DEC-315 option B).
 *
 * PATCH /v1/jobs/:id/cancel with { reason, driverDidWork }. On the stuck path the API cancels
 * the job, stops the driver's clock if it is running and pushes JOB_CANCELLED to the driver,
 * all in one transaction. It refuses a job that is no longer stuck, a job with a captured
 * load, and roles outside its manager-tier set; the modal shows the API's own sentence for
 * each refusal verbatim. That is why it uses useMutation + authedFetch directly:
 * useApiMutation keeps only the status and code, never fields[0].reason.
 *
 * driverDidWork is a required yes/no with NEITHER option pre-selected, so the dispatcher must
 * answer it. Structure and styling mirror DispatcherCloseModal. On success: invalidate
 * ['jobs'] (the stuck list ['jobs','stuck',h] and the job board), toast, call onCancelled.
 */

import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { authedFetch, useApiOpts } from '@lodex-pro/web-shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import type { StuckJobRow } from './use-stuck-jobs';

/**
 * The API requires a trimmed reason of 8+ on the stuck path because it becomes the STOPPED
 * timeclock reason (DB CHECK timeclock_events_reason_required_check, DEC-277).
 */
const REASON_MIN = 8;

const GENERIC_FAILURE = 'Could not cancel the job. Try again.';

interface ErrorEnvelope {
  readonly error?: {
    readonly code?: unknown;
    readonly message?: unknown;
    readonly fields?: ReadonlyArray<{ readonly path?: unknown; readonly reason?: unknown }>;
  };
}

/**
 * The refusal sentence to show: fields[0].reason when it is a string, else error.message,
 * else a generic line. Same precedence as features/import/api.ts readErrorMessage, WITHOUT
 * its "contains a space" filter — every reason this route returns is a sentence, and the
 * requirement is to show it verbatim.
 */
async function readCancelError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as ErrorEnvelope;
    const reason = body.error?.fields?.[0]?.reason;
    if (typeof reason === 'string') return reason;
    const message = body.error?.message;
    return typeof message === 'string' ? message : GENERIC_FAILURE;
  } catch {
    return GENERIC_FAILURE;
  }
}

export interface StuckJobCancelModalProps {
  readonly row: StuckJobRow | null;
  readonly open: boolean;
  readonly onClose: () => void;
  /** Called after a successful cancel so the parent can dismiss the modal. */
  readonly onCancelled: () => void;
}

const textareaStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '80px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
  resize: 'vertical',
};

const labelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--color-text-tertiary)',
  display: 'block',
  marginBottom: '4px',
};

const radioRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '16px',
  fontSize: '14px',
  color: 'var(--color-text-primary)',
};

export function StuckJobCancelModal({
  row,
  open,
  onClose,
  onCancelled,
}: StuckJobCancelModalProps): React.JSX.Element {
  const apiOpts = useApiOpts();
  const queryClient = useQueryClient();
  const [reason, setReason] = React.useState('');
  const [driverDidWork, setDriverDidWork] = React.useState<boolean | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setReason('');
      setDriverDidWork(null);
      setError(null);
    }
  }, [open]);

  const mutation = useMutation<unknown, Error, { jobId: string; payload: string }>({
    mutationFn: async ({ jobId, payload }) => {
      const res = await authedFetch(apiOpts, `/v1/jobs/${jobId}/cancel`, {
        method: 'PATCH',
        body: payload,
      });
      if (!res.ok) throw new Error(await readCancelError(res));
      return res.json() as Promise<unknown>;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      toast.success('Job cancelled');
      onCancelled();
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const reasonValid = reason.trim().length >= REASON_MIN;
  const canSubmit = reasonValid && driverDidWork !== null && !mutation.isPending;

  const handleConfirm = React.useCallback((): void => {
    if (!reasonValid || driverDidWork === null || row === null) return;
    setError(null);
    mutation.mutate({
      jobId: row.jobId,
      payload: JSON.stringify({ reason: reason.trim(), driverDidWork }),
    });
  }, [reasonValid, driverDidWork, row, reason, mutation]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next): void => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md">
        <DialogHeader>
          <DialogTitle>{`Cancel job — ${row?.haulId ?? 'job'}`}</DialogTitle>
        </DialogHeader>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', margin: 0 }}>
            This cancels the job and stops the driver&apos;s clock if it is running. The driver is
            notified.
          </p>

          <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend style={labelStyle}>Did the driver do any work on this job?</legend>
            <div style={radioRowStyle}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input
                  type="radio"
                  name="driver-did-work"
                  checked={driverDidWork === true}
                  onChange={(): void => setDriverDidWork(true)}
                />
                Yes
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input
                  type="radio"
                  name="driver-did-work"
                  checked={driverDidWork === false}
                  onChange={(): void => setDriverDidWork(false)}
                />
                No
              </label>
            </div>
          </fieldset>

          <label htmlFor="stuck-cancel-reason" style={labelStyle}>
            Reason
          </label>
          <textarea
            id="stuck-cancel-reason"
            value={reason}
            onChange={(e): void => setReason(e.target.value)}
            placeholder="Why is this job being cancelled? (min 8 characters)"
            style={textareaStyle}
            aria-label="Cancel reason"
          />
          {reason.trim().length > 0 && !reasonValid && (
            <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
              Reason must be at least {REASON_MIN} characters.
            </span>
          )}
        </div>

        {error !== null && (
          <p role="alert" style={{ fontSize: '13px', color: 'var(--color-danger)', margin: 0 }}>
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Keep job
          </Button>
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={handleConfirm}
            style={{
              backgroundColor: 'var(--color-danger)',
              color: 'var(--color-on-accent)',
              borderColor: 'var(--color-danger)',
              opacity: canSubmit ? 1 : 0.6,
            }}
          >
            {mutation.isPending ? 'Cancelling…' : 'Cancel job'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
